/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { NetworkError } from "@apollo/client/errors";
import { ErrorResponse } from "@apollo/client/link/error";
import { GraphQLError } from "graphql";
import ErrorAction from "../Commands/ErrorAction";
import AppSnackbarItem, { Code, Level } from "../Components/AppSnackbarContext/AppSnackbarItem";
import AppSnackbarItemFactory, {
    createInternalErrorItem,
    createNetworkErrorItem,
} from "../Components/AppSnackbarContext/AppSnackbarItemFactory";
import IAppSnackbar from "../Components/AppSnackbarContext/IAppSnackbar";
import { fixedT, translationKeys } from "../Internationalisierung/i18nBasis";
import ArrayUtil from "../Utils/ArrayUtil";

interface Reference {
    id: string;
    matchcode: string;
    typeName: string;
}

export default class CloudApolloErrorHandler {
    private static readonly _unexpectedExecutionErrorMessage = "Unexpected Execution Error";

    public handle(errorResponse: ErrorResponse, snackbar: IAppSnackbar): boolean {
        const source = this.getSource(errorResponse);
        const graphQLErrorsItems = this.getGraphQLErrorItems(source, errorResponse.graphQLErrors);
        const networkErrortItems = this.getNetworkErrorItems(source, errorResponse.networkError);
        const items = [...graphQLErrorsItems, ...networkErrortItems]
        if (ArrayUtil.isEmpty(items)) {
            snackbar.pushInternalError()
        } else {
            snackbar.push(items);
        }

        return this.shouldSuppressErrors(items);
    }

    private getGraphQLErrorItems(
        operationName: string,
        graphQLErrors: ReadonlyArray<GraphQLError> | undefined
    ): Array<AppSnackbarItem> {
        if (!graphQLErrors || ArrayUtil.isEmpty(graphQLErrors)) {
            return [];
        }

        return graphQLErrors.map(this.toGraphQLErrorItem.bind(null, operationName));
    }

    private toGraphQLErrorItem = (source: string, graphQLError: GraphQLError): AppSnackbarItem => {
        if (graphQLError.message === CloudApolloErrorHandler._unexpectedExecutionErrorMessage) {
            return createInternalErrorItem(source);
        }

        const path = Array.isArray(graphQLError.extensions?.validationPath)
            ? graphQLError.extensions.validationPath.map((item) => item.toString())
            : [];

        const reference = (!!graphQLError.extensions)? "reference" in graphQLError.extensions ? graphQLError.extensions.reference as Reference : null : null;
        const action = (!!graphQLError.extensions)? "action" in graphQLError.extensions ? graphQLError.extensions.action as ErrorAction : undefined : undefined;
        return new AppSnackbarItemFactory().create({
            source,
            message: graphQLError.message,
            level: (graphQLError.extensions?.level as Level) ?? "Error",
            code: (graphQLError.extensions?.code as Code) ?? "",
            path,
            referenceId: reference?.id ?? "",
            referenceMatchcode: reference?.matchcode ?? "",
            referenceTypeName: reference?.typeName ?? "",
            errorId: (graphQLError.extensions?.errorId as string) ?? "",
            action,
        });
    }

    private getNetworkErrorItems(
        source: string,
        error: NetworkError | undefined
    ): Array<AppSnackbarItem> {
        return !error ? [] : [createNetworkErrorItem(source)];
    }

    private getSource(errorResponse: ErrorResponse): string {
        const source = errorResponse.operation.operationName;
        if (!source) {
            throw new Error(fixedT(translationKeys.cloudApolloErrorHandler.unknownSource));
        }

        return source;
    }

    private shouldSuppressErrors(items: Array<AppSnackbarItem>): boolean {
        return items.every(item => item.level !== "Error");
    }
}
